"use client";

import { Trans } from "@lingui/react/macro";
import { FragmentOf, readFragment } from "gql.tada";
import { PlusIcon } from "lucide-react";
import { useParams, usePathname } from "next/navigation";
import { AdsBetaBadge } from "@/app/[lang]/[workspaceId]/ads/_components/ads-beta-badge";
import { SidebarGroupAction, SidebarMenu } from "@/components/ui/sidebar";
import { ADS_POLICIES } from "@/lib/authorization/policies/ads-analytics";
import { useCheckPolicy } from "@/lib/authorization/use-check-policy";
import CreateProjectDialog from "../../dialog/create-project-dialog";
import AdsSortableProjects from "./ads-sortable-projects";
import CollapsibleProjectGroup from "./collapsible-project-group";
import { AdsAnalyticsProjectItemFragment } from "./fragments";
import type { CollapsibleState } from "./types";

type Props = {
  workspaceId: string;
  projectsData: ReadonlyArray<
    FragmentOf<typeof AdsAnalyticsProjectItemFragment>
  >;
  initialGroupState: CollapsibleState;
  workspaceUsedColors?: string[];
};

export default function AdsSidebarGroup({
  workspaceId,
  projectsData,
  initialGroupState,
  workspaceUsedColors,
}: Props) {
  const currentPathname = usePathname();
  const { lang } = useParams<{ lang: string }>();
  const checkPolicy = useCheckPolicy();
  const validProjectsData = projectsData || [];

  const dataKey = validProjectsData
    .map((data) => {
      const project = readFragment(AdsAnalyticsProjectItemFragment, data);
      if (project?.adsAnalytics?.views) {
        return project.adsAnalytics.views
          .map((v) => `${v.id}:${v.name}`)
          .join(",");
      }
      return "";
    })
    .join("|");

  const canEditProjects = checkPolicy(ADS_POLICIES["ads-project:edit"]).ok;

  return (
    <CollapsibleProjectGroup
      groupType="ads"
      title={
        <span className="flex items-center gap-1.5">
          <Trans>ADS ANALYTICS</Trans>
          <AdsBetaBadge />
        </span>
      }
      initialState={initialGroupState}
      action={
        canEditProjects ? (
          <CreateProjectDialog
            workspaceId={workspaceId}
            type="ads"
            workspaceUsedColors={workspaceUsedColors}
            trigger={
              <SidebarGroupAction>
                <PlusIcon className="w-4 h-4" />
                <span className="sr-only">
                  <Trans>New Project</Trans>
                </span>
              </SidebarGroupAction>
            }
          />
        ) : undefined
      }
    >
      <SidebarMenu>
        <AdsSortableProjects
          key={dataKey}
          workspaceId={workspaceId}
          projectsData={validProjectsData}
          currentPathname={currentPathname}
          lang={lang}
        />
      </SidebarMenu>
    </CollapsibleProjectGroup>
  );
}
